import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Cpu, ArrowRight, XCircle } from 'lucide-react';
import SocialEngagementBlock from './SocialEngagementBlock';
import SquareApplePayButton, { squareVerifyBuyer } from '@/components/checkout/SquareApplePayButton';

const ENTRY_PRICE = 5;

function currentRigPeriod() {
  const now = new Date();
  const half = now.getMonth() < 6 ? 'H1' : 'H2';
  return `${now.getFullYear()}-${half}`;
}

// Paid £5 entry into the six-monthly "Win a Sim Rig" draw. Collects enhanced
// customer details and social engagement, then redirects to Stripe Checkout
// to take the £5 payment. The paid CompetitionEntry is created by the
// stripeCompetitionWebhook once payment succeeds.
export default function SimRigEntryForm() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', postcode: '',
    date_of_birth: '', address_line1: '', address_city: '',
    marketing_opt_in: false,
    liked_post: false, shared_post: false, followed_social: false,
    instagram_handle: '', facebook_handle: '', tiktok_handle: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [localPaid, setLocalPaid] = useState(false);

  const paidStatus = params.get('paid'); // 'success' | 'cancel' | null
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Synchronous form validation shared by the Stripe path and the Apple Pay
  // path. Returning a non-empty string blocks the Apple Pay sheet opening.
  const validateEntry = () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      return 'Please enter your first name, last name and email.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email address.';
    if (form.date_of_birth) {
      const age = (Date.now() - new Date(form.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) return 'You must be at least 18 years old to enter the sim rig draw.';
    }
    if (!form.address_line1.trim() || !form.address_city.trim() || !form.postcode.trim()) {
      return 'Please enter your full postal address — we need it to deliver the rig if you win.';
    }
    if (window.self !== window.top) {
      return 'Checkout only works from the published app. Please open the competition page in a new tab to enter.';
    }
    return '';
  };

  const handlePay = async () => {
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('Please enter your first name, last name and email.'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address.'); return; }
    if (form.date_of_birth) {
      const age = (Date.now() - new Date(form.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) { setError('You must be at least 18 years old to enter the sim rig draw.'); return; }
    }
    if (!form.address_line1.trim() || !form.address_city.trim() || !form.postcode.trim()) {
      setError('Please enter your full postal address — we need it to deliver the rig if you win.'); return;
    }

    // Stripe Checkout can't run inside the builder iframe — only on the published app.
    if (window.self !== window.top) {
      setError('Checkout only works from the published app. Please open the competition page in a new tab to enter.');
      return;
    }

    setLoading(true);
    try {
      const res = await base44.functions.invoke('purchaseCompetitionEntryStripe', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        postcode: form.postcode,
        date_of_birth: form.date_of_birth,
        address_line1: form.address_line1,
        address_city: form.address_city,
        instagram_handle: form.instagram_handle,
        facebook_handle: form.facebook_handle,
        tiktok_handle: form.tiktok_handle,
        liked_post: form.liked_post,
        shared_post: form.shared_post,
        followed_social: form.followed_social,
        marketing_opt_in: form.marketing_opt_in,
        success_url: `${window.location.origin}/competition?paid=success`,
        cancel_url: `${window.location.origin}/competition?paid=cancel`,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError(res.data?.error || 'Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(err?.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  // Apple Pay path — tokenizes on click (Apple requirement), then SCA-verifies
  // and charges £5 via the Square purchaseCompetitionEntry function.
  const handleApplePayToken = async (token) => {
    setLoading(true);
    setError('');
    const verificationDetails = {
      amount: ENTRY_PRICE.toFixed(2),
      currencyCode: 'GBP',
      intent: 'CHARGE',
      billingContact: {
        givenName: form.first_name,
        familyName: form.last_name,
        email: form.email,
        phone: form.phone || undefined,
        addressLines: [form.address_line1].filter(Boolean),
        city: form.address_city,
        postalCode: form.postcode,
        countryCode: 'GB',
      },
    };
    let verifiedToken;
    try {
      verifiedToken = await squareVerifyBuyer(token, verificationDetails);
    } catch (err) {
      setError(err?.message || 'Payment verification failed. Please try again.');
      setLoading(false);
      return;
    }
    try {
      const res = await base44.functions.invoke('purchaseCompetitionEntry', {
        sourceId: token,
        verificationToken: verifiedToken,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        postcode: form.postcode,
        date_of_birth: form.date_of_birth,
        address_line1: form.address_line1,
        address_city: form.address_city,
        instagram_handle: form.instagram_handle,
        facebook_handle: form.facebook_handle,
        tiktok_handle: form.tiktok_handle,
        liked_post: form.liked_post,
        shared_post: form.shared_post,
        followed_social: form.followed_social,
        marketing_opt_in: form.marketing_opt_in,
        experience_preference: 'any',
      });
      if (res.data?.success) {
        setLocalPaid(true);
      } else {
        setError(res.data?.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (paidStatus === 'success' || localPaid) {
    return (
      <div className="border border-green-500/30 bg-green-500/5 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
        <h2 className="text-white font-black text-2xl uppercase mb-2">You're in the draw!</h2>
        <p className="text-white/50 text-sm max-w-md mx-auto">
          Your £5 entry for the {currentRigPeriod()} sim rig draw has been recorded. The winner is drawn at random at the
          end of the draw period — keep an eye on your inbox. Good luck!
        </p>
        <Link to="/competition" className="mt-5 inline-block text-primary hover:text-primary/80 text-xs font-bold tracking-widest underline">
          BACK TO COMPETITION
        </Link>
      </div>
    );
  }

  if (paidStatus === 'cancel') {
    return (
      <div className="border border-amber-500/30 bg-amber-500/5 p-8 text-center">
        <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-white font-black text-2xl uppercase mb-2">Payment cancelled</h2>
        <p className="text-white/50 text-sm max-w-md mx-auto">
          Your payment wasn't completed and no charge was made. You can try again whenever you're ready.
        </p>
        <Link to="/competition" className="mt-5 inline-block text-primary hover:text-primary/80 text-xs font-bold tracking-widest underline">
          TRY AGAIN
        </Link>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3">
        <div className="border border-white/10 bg-[#111115] p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Cpu className="w-4 h-4 text-primary" />
            <h2 className="text-white font-bold text-lg">£5 entry — Win a Sim Rig</h2>
          </div>

          {error && <div className="mb-4 px-4 py-3 bg-red-500/5 border border-red-500/30 text-red-300 text-xs">{error}</div>}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="FIRST NAME *"><input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className="seo-input" autoComplete="given-name" /></Field>
            <Field label="LAST NAME *"><input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className="seo-input" autoComplete="family-name" /></Field>
            <Field label="EMAIL *" full><input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="seo-input" autoComplete="email" /></Field>
            <Field label="DATE OF BIRTH *"><input type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} className="seo-input" /></Field>
            <Field label="PHONE"><input value={form.phone} onChange={(e) => setField('phone', e.target.value)} className="seo-input" autoComplete="tel" /></Field>
            <Field label="ADDRESS LINE 1 *" full><input value={form.address_line1} onChange={(e) => setField('address_line1', e.target.value)} className="seo-input" autoComplete="address-line1" /></Field>
            <Field label="CITY / TOWN *"><input value={form.address_city} onChange={(e) => setField('address_city', e.target.value)} className="seo-input" /></Field>
            <Field label="POSTCODE *"><input value={form.postcode} onChange={(e) => setField('postcode', e.target.value)} className="seo-input" autoComplete="postal-code" /></Field>
          </div>

          <SocialEngagementBlock form={form} setForm={setForm} />

          <label className="flex items-start gap-3 mt-5 cursor-pointer">
            <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setField('marketing_opt_in', e.target.checked)} className="mt-1 accent-[#e11d2a]" />
            <span className="text-white/40 text-xs leading-relaxed">
              Yes, send me Motion Reality news, offers and competition reminders. (Optional.)
            </span>
          </label>

          <p className="text-white/30 text-[11px] leading-relaxed mt-5">
            By entering you agree to the competition <Link to="/terms-conditions" className="text-primary/70 hover:text-primary underline">terms</Link>. Open to UK residents aged 18+. Winner drawn at random every six months and contacted by email. £5 per entry — no limit on entries.
          </p>

          <SquareApplePayButton
            amount={ENTRY_PRICE}
            label="Motion Reality"
            onToken={handleApplePayToken}
            onError={setError}
            validate={validateEntry}
            disabled={loading}
            containerId="sq-comp-applepay"
            onAvailabilityChange={setApplePayAvailable}
          />

          {applePayAvailable && (
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/30 text-[10px] font-bold tracking-widest">OR PAY BY CARD</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          )}

          <button
            onClick={handlePay}
            disabled={loading}
            className="mt-6 w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-4 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> STARTING CHECKOUT…</> : <>PAY £{ENTRY_PRICE.toFixed(2)} & ENTER <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-white/20 text-xs mt-3">Prefer card? You'll be redirected to Stripe's secure checkout to complete your £{ENTRY_PRICE.toFixed(2)} payment. We never see or store your card details.</p>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="border border-primary/30 bg-primary/[0.04] p-6">
          <div className="flex items-center gap-2 mb-3">
            <Cpu className="w-4 h-4 text-primary" />
            <p className="text-primary text-[10px] font-bold tracking-widest">THE PRIZE</p>
          </div>
          <p className="text-white font-black text-xl uppercase">A Full Sim Racing Rig</p>
          <p className="text-white/40 text-xs mt-2 leading-relaxed">
            One lucky winner receives a complete pro-spec sim racing rig — direct-drive wheelbase, load-cell pedals,
            cockpit and triple-screen setup. Drawn at random every six months from all paid entries.
          </p>
        </div>
        <div className="border border-white/10 bg-[#111115] p-6">
          <p className="text-white/50 text-[10px] font-bold tracking-widest mb-3">HOW IT WORKS</p>
          <ul className="space-y-2 text-white/40 text-xs">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" /> £5 per entry — enter as many times as you like</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" /> Entries are pooled into the current 6-month draw period</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" /> One random winner drawn at the end of each period</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" /> Winner notified by email and announced on socials</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}